import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Input,
  Pagination,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeleton,
  Tag,
  useToast,
} from "@egaranti/components";

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ExtendedWarrantyService from "@/services/extendedWarrantyService";

import useExtendedWarrantyStore from "@/stores/useExtendedWarranty";

import { useDebounce } from "@/components/ui/BulkImportWizard/hooks/useDebounce";

import useSubMerchantTitles from "@/hooks/useSubMerchantTitles";
import EmptyTable from "@/components/shared/EmptyTable";
import PageSizeSelect from "@/components/shared/pageSizeSelect";

import PlusIcon from "@/assets/icons/plus.svg?react";

const ANY_VALUE = "__all__";

const Packages = () => {
  const { t } = useTranslation();
  const { toast } = useToast();

  const navigate = useNavigate();
  const [isActivationOpen, setIsActivationOpen] = useState(false);
  const [codeCount, setCodeCount] = useState(1);
  const [isGeneratingCode, setIsGeneratingCode] = useState(false);
  const [isExportingCode, setIsExportingCode] = useState(false);

  const {
    additionalServicePlans: packages,
    loading,
    planFilters,
    pagination,
    resetPlanFilters,
    updatePlanFilters,
    handlePageChange,
    handlePageSizeChange,
  } = useExtendedWarrantyStore();

  const { showMerchantColumn, getMerchantTitle } = useSubMerchantTitles();

  const [nameInput, setNameInput] = useState("");
  const [packageCodeInput, setPackageCodeInput] = useState("");
  const debouncedName = useDebounce(nameInput, 400);
  const debouncedPackageCode = useDebounce(packageCodeInput, 400);
  const isFirstRun = useRef(true);

  useEffect(() => {
    resetPlanFilters();
  }, []);

  useEffect(() => {
    if (isFirstRun.current) {
      isFirstRun.current = false;
      return;
    }
    updatePlanFilters({
      name: debouncedName,
      packageCode: debouncedPackageCode,
      page: 1,
    });
  }, [debouncedName, debouncedPackageCode]);

  const handleSelectFilter = (key, value) => {
    updatePlanFilters({
      [key]: value === ANY_VALUE ? null : value,
      page: 1,
    });
  };

  const handleGenerateCode = async () => {
    try {
      setIsGeneratingCode(true);
      await ExtendedWarrantyService.generateActivationCodes(
        parseInt(codeCount),
      );

      toast({
        title: t("extendedWarranties.activationCode", "Aktivasyon Kodu"),
        description: t(
          "extendedWarranties.codeGenerated",
          "Kodlar başarıyla oluşturuldu",
        ),
        variant: "success",
      });
      handleExportCodes();
    } catch (error) {
      toast({
        title: t("extendedWarranties.activationCode", "Aktivasyon Kodu"),
        description: t(
          "extendedWarranties.codeGenerationFailed",
          "Kodlar oluşturulamadı",
        ),
        variant: "error",
      });
    } finally {
      setIsGeneratingCode(false);
    }
  };

  const handleExportCodes = async () => {
    try {
      setIsExportingCode(true);
      const blob = await ExtendedWarrantyService.exportActivationCodes();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "activation-codes.xlsx";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast({
        title: t("extendedWarranties.activationCode", "Aktivasyon Kodu"),
        description: t("extendedWarranties.codeExported", "Kodlar indirildi"),
        variant: "success",
      });
    } catch (error) {
      toast({
        title: t("extendedWarranties.activationCode", "Aktivasyon Kodu"),
        description: t(
          "extendedWarranties.codeExportFailed",
          "Kodlar indirilemedi",
        ),
        variant: "error",
      });
    } finally {
      setIsExportingCode(false);
    }
  };

  // map for status, with variant for tag component
  const statusMap = {
    ACTIVE: { label: t("extendedWarranties.active"), variant: "green" },
    PASSIVE: { label: t("extendedWarranties.passive"), variant: "gray" },
  };

  const planTypeMap = {
    BASIC: {
      label: t("extendedWarranties.planTypeBasic", "Standart"),
      classes: "bg-blue-100 text-blue-700",
    },
    BAREM: {
      label: t("extendedWarranties.planTypeBarem", "Baremli"),
      classes: "bg-purple-100 text-purple-700",
    },
  };

  return (
    <main className="min-h-full bg-[#F9FAFC] p-4">
      <div className="flex items-center justify-end gap-4 pb-4"></div>
      <div className="rounded-lg">
        <div className="flex justify-end gap-2 pb-6">
          <Dialog open={isActivationOpen} onOpenChange={setIsActivationOpen}>
            <DialogTrigger asChild>
              <Button variant="secondaryColor">
                {t("extendedWarranties.activationCode", "Aktivasyon Kodu")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white">
              <DialogHeader>
                <DialogTitle>
                  {t("extendedWarranties.activationCode", "Aktivasyon Kodu")}
                </DialogTitle>
                <DialogDescription>
                  {t(
                    "extendedWarranties.activationCodeDescription",
                    "Aktivasyon kodu oluşturabilir veya mevcut kodları indirebilirsiniz",
                  )}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">
                    {t("extendedWarranties.codeCount")}
                  </label>
                  <Input
                    type="number"
                    value={codeCount}
                    onChange={(e) => setCodeCount(e.target.value)}
                    min="1"
                    max="100"
                  />
                </div>
                <div className="flex gap-2 pt-12">
                  <Button
                    onClick={handleGenerateCode}
                    className="flex-1"
                    disabled={isGeneratingCode}
                  >
                    {isGeneratingCode
                      ? t("common.loading", "Yükleniyor...")
                      : t("extendedWarranties.generateCode", "Kod Oluştur")}
                  </Button>
                  <Button
                    onClick={handleExportCodes}
                    variant="secondaryGray"
                    className="flex-1"
                    disabled={isExportingCode}
                  >
                    {isExportingCode
                      ? t("common.loading", "Yükleniyor...")
                      : t("extendedWarranties.downloadCodes", "Kodları İndir")}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          <Button onClick={() => navigate("/extended-warranties/packages/new")}>
            <PlusIcon />
            <span>{t("extendedWarrantiesPage.newPackage")}</span>
          </Button>
        </div>
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("extendedWarranties.name")}
              </label>
              <Input
                placeholder={t("extendedWarranties.name")}
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("extendedWarranties.planType", "Plan Türü")}
              </label>
              <Select
                value={planFilters.planType ?? ANY_VALUE}
                onValueChange={(v) => handleSelectFilter("planType", v)}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t(
                      "extendedWarranties.planType",
                      "Plan Türü",
                    )}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_VALUE}>
                    {t("common.all", "Tümü")}
                  </SelectItem>
                  <SelectItem value="BASIC">
                    {t("extendedWarranties.planTypeBasic", "Standart")}
                  </SelectItem>
                  <SelectItem value="BAREM">
                    {t("extendedWarranties.planTypeBarem", "Baremli")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("extendedWarranties.planCode", "Plan Kodu")}
              </label>
              <Input
                placeholder={t("extendedWarranties.packageCode", "Plan Kodu")}
                value={packageCodeInput}
                onChange={(e) => setPackageCodeInput(e.target.value)}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-sm font-medium text-gray-700">
                {t("extendedWarranties.status")}
              </label>
              <Select
                value={planFilters.status ?? ANY_VALUE}
                onValueChange={(v) => handleSelectFilter("status", v)}
              >
                <SelectTrigger>
                  <SelectValue
                    placeholder={t("extendedWarranties.status")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY_VALUE}>
                    {t("common.all", "Tümü")}
                  </SelectItem>
                  <SelectItem value="ACTIVE">
                    {t("extendedWarranties.active")}
                  </SelectItem>
                  <SelectItem value="PASSIVE">
                    {t("extendedWarranties.passive")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>
        {/*
          EPS-5120 (8. bulgu) — liste telefonda masaustu tablosunu aynen
          gosteriyordu. Replikada olculdu (390 px): 4 sutundan yalniz 2'si
          ekranda; Plan Kodu ve Durum sagda kaliyor, tablo 146 px yana
          kaydirilabiliyor. Bireysel Musteriler'de onaylanan desen uygulandi.
        */}
        <div className="hidden overflow-x-auto rounded-lg bg-white pb-24 md:block">
          <Table className="w-full">
            <TableHeader>
              <TableRow>
                <TableHead>{t("extendedWarranties.name")}</TableHead>
                <TableHead>
                  {t("extendedWarranties.planType", "Plan Türü")}
                </TableHead>
                <TableHead>
                  {t("extendedWarranties.planCode", "Plan Kodu")}
                </TableHead>
                <TableHead>{t("extendedWarranties.status")}</TableHead>
                {/* EPS-5027: ana bayide liste alt bayilerin planlarini da icerir. */}
                {showMerchantColumn && (
                  <TableHead>{t("subMerchantsPage.title", "Alt Bayi")}</TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableSkeleton colSpan={showMerchantColumn ? 5 : 4} />
              ) : packages?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={showMerchantColumn ? 5 : 4} className="p-0">
                    <EmptyTable
                      title={t("common.emptyTableNoData")}
                      desc={t("common.emptyTableNoDataDesc")}
                    />
                  </TableCell>
                </TableRow>
              ) : (
                packages.map((pkg) => (
                  <TableRow
                    key={pkg.id}
                    onClick={() =>
                      navigate(`/extended-warranties/packages/${pkg.id}`)
                    }
                    className="cursor-pointer"
                  >
                    <TableCell>
                      {pkg.name ||
                        (pkg.planType === "BAREM"
                          ? `${t("extendedWarranties.planTypeBarem", "Baremli")} #${pkg.id}`
                          : "-")}
                    </TableCell>
                    <TableCell>
                      {planTypeMap[pkg.planType] ? (
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                            planTypeMap[pkg.planType].classes
                          }`}
                        >
                          {planTypeMap[pkg.planType].label}
                        </span>
                      ) : (
                        pkg.planType
                      )}
                    </TableCell>
                    {/* EPS-4970: Baremli planda plan satırının paket kodu, Standart
                       Planda aralıkların ilk kodu gösterilir (kodlar alt tabloda,
                       aralık bazında tutuluyor). Birden fazlaysa "+N" ile belirtilir. */}
                    <TableCell>
                      {pkg.packageCode ||
                        (pkg.rateBarems?.length
                          ? `${pkg.rateBarems[0].packageCode}${
                              pkg.rateBarems.length > 1
                                ? ` +${pkg.rateBarems.length - 1}`
                                : ""
                            }`
                          : "-")}
                    </TableCell>
                    <TableCell>
                      {pkg.status && statusMap[pkg.status] ? (
                        <Tag size="sm" variant={statusMap[pkg.status].variant}>
                          {statusMap[pkg.status].label}
                        </Tag>
                      ) : (
                        "-"
                      )}
                    </TableCell>
                    {showMerchantColumn && (
                      <TableCell>{getMerchantTitle(pkg.merchantId)}</TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Mobil kart listesi */}
        <div className="flex flex-col divide-y divide-gray-100 rounded-lg bg-white pb-24 md:hidden">
          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <PaketKartiIskelet key={i} />)
          ) : packages?.length === 0 ? (
            <EmptyTable
              title={t("common.emptyTableNoData")}
              desc={t("common.emptyTableNoDataDesc")}
            />
          ) : (
            packages.map((pkg) => (
              <PaketKartiMobil
                key={pkg.id}
                pkg={pkg}
                planTypeMap={planTypeMap}
                statusMap={statusMap}
                altBayiBasligi={showMerchantColumn ? getMerchantTitle(pkg.merchantId) : null}
                onClick={() => navigate(`/extended-warranties/packages/${pkg.id}`)}
                t={t}
              />
            ))
          )}
        </div>
      </div>
      <div className="sticky bottom-0 left-0 right-0 z-10 border-t border-gray-200 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-4 px-4 py-3">
          <div className="text-sm text-gray-600">
            {!loading &&
              t("common.totalRecords", "Toplam {{count}} kayıt", {
                count: pagination.totalElements || 0,
              })}
          </div>
          <div className="flex items-center gap-6">
            {pagination.totalPages > 1 && (
              <Pagination
                totalPages={pagination.totalPages}
                currentPage={pagination.currentPage}
                pageSize={planFilters.size}
                onPageChange={handlePageChange}
              />
            )}
            <PageSizeSelect
              onPageSizeChange={handlePageSizeChange}
              value={planFilters.size}
            />
          </div>
        </div>
      </div>
    </main>
  );
};


/**
 * EPS-5120 (8. bulgu) — Paketler listesinin mobil kart gorunumu.
 *
 * Tabloda 4 (ana bayide 5) sutun var, 390 px'de yalniz 2'si goruluyordu.
 * Kartta tum alanlar: ad (baslik), durum rozeti, plan turu, plan kodu ve
 * varsa alt bayi.
 */
const PaketKartiMobil = ({
  pkg,
  planTypeMap,
  statusMap,
  altBayiBasligi,
  onClick,
  t,
}) => {
  const ad =
    pkg.name ||
    (pkg.planType === "BAREM"
      ? `${t("extendedWarranties.planTypeBarem", "Baremli")} #${pkg.id}`
      : "-");
  const kod =
    pkg.packageCode ||
    (pkg.rateBarems?.length
      ? `${pkg.rateBarems[0].packageCode}${
          pkg.rateBarems.length > 1 ? ` +${pkg.rateBarems.length - 1}` : ""
        }`
      : "-");
  return (
    <div
      role="button"
      onClick={onClick}
      className="flex cursor-pointer flex-col gap-2 px-4 py-4 active:bg-[#F9FAFC]"
    >
      <div className="flex items-start justify-between gap-3">
        <span className="min-w-0 flex-1 truncate font-medium text-[#111729]">
          {ad}
        </span>
        {pkg.status && statusMap[pkg.status] ? (
          <Tag size="sm" variant={statusMap[pkg.status].variant}>
            {statusMap[pkg.status].label}
          </Tag>
        ) : null}
      </div>
      <dl className="space-y-0.5 text-sm text-[#677389]">
        <div className="flex gap-2">
          <dt className="shrink-0">
            {t("extendedWarranties.planType", "Plan Türü")}:
          </dt>
          <dd className="truncate">
            {planTypeMap[pkg.planType]?.label || pkg.planType || "-"}
          </dd>
        </div>
        <div className="flex gap-2">
          <dt className="shrink-0">
            {t("extendedWarranties.planCode", "Plan Kodu")}:
          </dt>
          <dd className="truncate">{kod}</dd>
        </div>
        {altBayiBasligi != null && (
          <div className="flex gap-2">
            <dt className="shrink-0">
              {t("subMerchantsPage.title", "Alt Bayi")}:
            </dt>
            <dd className="truncate">{altBayiBasligi}</dd>
          </div>
        )}
      </dl>
    </div>
  );
};

/** Kart yuksekligiyle ayni iskelet. */
const PaketKartiIskelet = () => (
  <div className="flex flex-col gap-2 px-4 py-4">
    <div className="h-4 w-1/2 animate-pulse rounded bg-gray-200" />
    <div className="h-3 w-2/3 animate-pulse rounded bg-gray-100" />
    <div className="h-3 w-1/2 animate-pulse rounded bg-gray-100" />
  </div>
);

export default Packages;
